'use client'

import { Modal } from "@mantine/core"
import { LoadingSpinner } from "../misc/loadingSpinner"
import ModalTemplate from "./templates/modalTemplate"
import { EmptyCareSchedule, EmptyPlant, FertilizerType, IPlant, SunlightRequirements } from "@/models/types/plant";
import { useModalStore } from "@/context/modalStore";
import { CreatePlant } from "@/utils/planthelpers/create";
import { useState } from "react";
import { Session } from "next-auth";
import { toast } from "sonner";
import { useStateStore } from "@/context/stateStore";
import { useRouter } from "next/navigation";
import PlantFields from "../forms/fields/plantFields";
import { useForm } from "@mantine/form";

export default function NewPlantModal({ session, handleUpdate }: { session: Session | null, handleUpdate: () => Promise<void> }) {

    const [loading, setLoading] = useState<boolean>(false);
    const router = useRouter();
    const openNewPlantModal = useModalStore(state => state.openNewPlantModal);
    const setOpenNewPlantModal = useModalStore(state => state.setOpenNewPlantModal);
    const plantForm = useForm({
        mode: 'uncontrolled',
        initialValues: {
            plant: {
                _id: '',
                ownerEmail: '',
                sourceCatalogPlantId: null,
                name: '',
                imageUrl: null,
                description: null,
                timeToGrow: {
                    min: null,
                    max: null,
                },
                season: null,
                seasonToAvoid: null,
                waterFrequency: null,
                careSchedule: EmptyCareSchedule,
                soilType: null,
                sunlight: {
                    _id: '',
                    requirement: null,
                    hoursOfSunlight: null,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                } as SunlightRequirements,
                fertilizer: {
                    _id: '',
                    name: null,
                    type: null,
                    npkRatio: {
                        nitrogen: 0,
                        phosphorus: 0,
                        potassium: 0,
                    },
                    createdAt: new Date(),
                    updatedAt: new Date(),
                } as FertilizerType,
                isIndoor: false,
                isOutdoor: false,
                createdAt: new Date(),
                updatedAt: new Date(),
                type: 'plant' as 'plant',
            } as IPlant
        },
        validate: {
            plant: (value) => !value ? 'Plant name is required' : null
        }
    });

    const resetZoom = useStateStore(state => state.handleZoomReset);
    const width = useStateStore(state => state.widthQuery);

    const handleCancel = () => {
        plantForm.reset();
        plantForm.clearErrors();
        setOpenNewPlantModal(false);
        resetZoom(width, false);
        setLoading(false);
    }

    const HandleCreatePlant = async () => {
        setLoading(true);

        try {
            plantForm.clearErrors();

            if (!session) {
                toast.warning("You must be signed in!");
                setLoading(false);
                return;
            }

            const sessionUser = session.user;

            if (!sessionUser) {
                toast.error('User session is invalid or missing email');
                setLoading(false);
                return;
            }

            const validation = plantForm.validate();

            if (validation.hasErrors) {
                toast.error('Please fix the errors in the form');
                setLoading(false);
                return;
            }

            const vals = plantForm.getValues();
            const plant = vals.plant;
            if (!plant || !plant.name || plant.name.trim() === '') {
                toast.error('Plant name is required');
                setLoading(false);
                return;
            }

            const plantToPass = {
                name: plant.name,
                ownerEmail: sessionUser.email,
                sourceCatalogPlantId: plant.sourceCatalogPlantId ?? null,
                imageUrl: plant.imageUrl ? plant.imageUrl : null,
                description: plant.description ? plant.description : null,
                timeToGrow: {
                    min: plant.timeToGrow && typeof plant.timeToGrow === 'object' && plant.timeToGrow.min ? plant.timeToGrow.min.toString() : null,
                    max: plant.timeToGrow && typeof plant.timeToGrow === 'object' && plant.timeToGrow.max ? plant.timeToGrow.max.toString() : null,
                },
                season: plant.season ? plant.season : null,
                seasonToAvoid: plant.seasonToAvoid ? plant.seasonToAvoid : null,
                waterFrequency: plant.waterFrequency ? plant.waterFrequency : null,
                careSchedule: {
                    wateringIntervalDays: plant.careSchedule?.wateringIntervalDays ?? null,
                    lastWateredAt: plant.careSchedule?.lastWateredAt ?? null,
                    nextWateringAt: plant.careSchedule?.nextWateringAt ?? null,
                    reminderEnabled: plant.careSchedule?.reminderEnabled ?? false,
                },
                soilType: plant.soilType ? plant.soilType : null,
                _id: '',
                fertilizer: {
                    _id: `fert-${plant.name.toLowerCase()}`,
                    name: plant.fertilizer ? plant.fertilizer.name : null,
                    type: plant.fertilizer ? plant.fertilizer.type : null,
                    npkRatio: {
                        nitrogen: plant.fertilizer && plant.fertilizer.npkRatio ? plant.fertilizer.npkRatio.nitrogen : 0,
                        phosphorus: plant.fertilizer && plant.fertilizer.npkRatio ? plant.fertilizer.npkRatio.phosphorus : 0,
                        potassium: plant.fertilizer && plant.fertilizer.npkRatio ? plant.fertilizer.npkRatio.potassium : 0,
                    },
                    createdAt: new Date(),
                    updatedAt: new Date(),
                } as FertilizerType,
                sunlight: {
                    _id: `sun-${plant.name.toLowerCase()}`,
                    requirement: plant.sunlight ? plant.sunlight.requirement : null,
                    hoursOfSunlight: plant.sunlight ? plant.sunlight.hoursOfSunlight : null,
                    createdAt: new Date(),
                    updatedAt: new Date(),
                } as SunlightRequirements,
                isIndoor: plant.isIndoor ? plant.isIndoor : false,
                isOutdoor: plant.isOutdoor ? plant.isOutdoor : false,
                updatedAt: new Date(),
                createdAt: new Date(),
                type: 'plant',
            } as IPlant;

            const urlToUse = process.env.NEXT_PUBLIC_BASE_URL ? process.env.NEXT_PUBLIC_BASE_URL as string : '';

            let createPlant = await CreatePlant({ plant: plantToPass, user: sessionUser.email, urlToUse: urlToUse }) as { status: boolean, message: string };

            let attemptStatus = createPlant ? createPlant.status : false;

            if (attemptStatus !== true) {
                toast.error('Error creating plant attempt');
                setLoading(false);
                return;
            }

            toast.success('Plant created successfully!');
            await handleUpdate();
            router.refresh();
            handleCancel();
        } catch (error) {
            setLoading(false);
            return;
        }
    }

    return (
        <Modal
            opened={openNewPlantModal}
            onClose={handleCancel}
            title="New Plant"
            centered
            size={'xl'}
            overlayProps={{
                backgroundOpacity: 0.55, blur: 3, className: 'drop-shadow-xl'
            }}
            lockScroll={true}
            styles={{
                body: { height: '70vh', display: 'flex', flexDirection: 'column' },
                content: { height: 'auto' }
            }}
        >
            <ModalTemplate subtitle={null}>
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <form id="modalPlantForm" onSubmit={plantForm.onSubmit(HandleCreatePlant)} onAbort={handleCancel} className="w-full h-full flex flex-col">
                        <div className="flex-1 min-h-0 overflow-auto scrollbar-thin scrollbar-webkit py-1 px-1 sm:py-2 sm:px-2 bg-slate-100/70 rounded-md shadow-[inset_0_2px_8px_rgba(0,0,0,0.10),inset_0_-2px_8px_rgba(0,0,0,0.10)] border border-gray-300">
                            <PlantFields plant={EmptyPlant} plantForm={plantForm} />
                        </div>
                        <section className="flex-shrink-0 flex flex-row w-full justify-evenly items-center pt-3 bg-white border-t border-gray-200 mt-2">
                            <button type="button" onClick={handleCancel} className="cursor-pointer border border-neutral-200 rounded-md hover:bg-neutral-200 p-2 w-1/5 sm:w-1/3 md:w-1/4 lg:w-1/5">
                                Cancel
                            </button>
                            <button type='submit' className="cursor-pointer border border-neutral-200 rounded-md hover:bg-blue-200 bg-blue-400 p-2 w-1/5 sm:w-1/3 md:w-1/4 lg:w-1/5">
                                Create
                            </button>
                        </section>
                    </form>
                )}
            </ModalTemplate>
        </Modal>
    )
}
